import type { Editor } from '@tiptap/react';
import { ReactRenderer } from '@tiptap/react';
import { type MutableRefObject, useRef } from 'react';
import tippy, { type Instance as TippyInstance } from 'tippy.js';
import { useFetch } from '@/app/hooks';
import type {
  ElementModality,
  ImagePrompt,
  MentionItem,
  ReferencePrompt,
  TextPromptWithInputItem,
  VideoPrompt,
} from '@/app/types';
import { editorJsonToTextPrompt, extractMentionsIdsFromEditor, getMentionColor } from '@/app/utils';
import { materialsListPersonalElements } from '../apis';
import { MentionList } from '../components/MentionList';
import { CustomMention } from '../extensions/CustomMention';
import type { ViduV1Element } from '../types';

const SUGGESTION_PAGE_SIZE = 20;

interface UseCustomMentionProps {
  previousMentionsIds: MutableRefObject<string[]>;
  onBeforeAddMention?: (mentionItem: MentionItem) => boolean;
  /** mention 节点插入编辑器后触发，参数是命中 suggestion 列表的完整条目（含 preview/type 等元信息） */
  onMentionInsert?: (mentionItem: MentionItem) => void;
  onBeforeDeleteMention?: (mentionsIdsToDelete: string[], updatedContent: TextPromptWithInputItem) => boolean;
  customSearchFn?: (query: string) => Promise<MentionItem[]>;
  customMentionOptions?: Partial<Parameters<typeof CustomMention.configure>[0]>;
  /** 获取额外追加的元素，用于显示在@提示框中 */
  getExtraItems?: () => ReferencePrompt[];
  enableRemoteSearch?: boolean;
  /** 提示框的 z-index，默认为 49 */
  zIndex?: number;
  /** 提示框挂载容器 */
  popupContainer?: HTMLElement | (() => HTMLElement) | null;

  /** 指定 mention 节点的背景色，默认根据类型区分 */
  mentionColor?: string;
  /** 透传给 MentionNodeView 的类名，用于覆盖默认样式 */
  mentionClassName?: string;
  /** 透传给 MentionNodeView 的视觉模式 */
  mentionVariant?: 'default' | 'media-chip';
  /** 是否禁用 tooltip，默认为 false */
  disableTooltip?: boolean;
  /** 允许的 modality 筛选 */
  allowedModalities?: Array<ElementModality>;
}

export const useCustomMention = ({
  previousMentionsIds,
  onBeforeAddMention,
  onMentionInsert,
  onBeforeDeleteMention,
  customSearchFn,
  customMentionOptions = {},
  getExtraItems,
  enableRemoteSearch = true,
  zIndex = 49,
  popupContainer,
  mentionColor,
  mentionClassName,
  mentionVariant,
  disableTooltip = false,
  allowedModalities,
}: UseCustomMentionProps) => {
  // 使用ref以确保在组件重新渲染时保持相同的搜索函数
  const lastSearchFn = useRef(customSearchFn);
  lastSearchFn.current = customSearchFn;

  const mentionListRef = useRef<MentionItem[]>([]);

  const { runAsync: defaultSearchEntities } = useFetch(
    async (query: string) => {
      let apiElements: MentionItem[] = [];

      // 只有登录用户才能请求个人主体库
      if (enableRemoteSearch) {
        const data = await materialsListPersonalElements({
          query: {
            name_like: query,
            'pager.pagesz': SUGGESTION_PAGE_SIZE,
            include_collected: true,
            modalities: allowedModalities,
          },
        });
        apiElements = (data.data?.elements ?? []).map((element) => ({
          id: element.id || '',
          label: element.name || '',
          preview:
            element.modality === 'video'
              ? (element.components?.[0]?.cover_uri ?? '')
              : element.components?.[0]?.content,
          element,
        }));
      }

      const filteredAppend: MentionItem[] = (
        (getExtraItems?.() || []).filter(
          (appendItem) => !apiElements.some((apiElement) => apiElement.id === appendItem.id),
        ) ?? []
      ).map((item) => ({
        id: item.id,
        label: item.name,
        preview:
          item.type === 'image'
            ? (item.prompt as ImagePrompt)?.selectedImagePreview
            : item.type === 'video'
              ? (item.prompt as VideoPrompt)?.coverImagePreview
              : (item.prompt as ViduV1Element)?.components?.[0]?.content,
        element: item.type === 'element' ? (item.prompt as ViduV1Element) : undefined,
        type: item.type,
        prompt: item.prompt,
      }));
      mentionListRef.current = filteredAppend.concat(apiElements);
      return mentionListRef.current;
    },
    {
      debounceWait: 500,
      manual: true,
    },
  );

  const onUpdateCallback = (
    editor: Editor,
    lastValidContent: MutableRefObject<string | null>,
    isInternalUpdate: MutableRefObject<boolean>,
  ) => {
    const currentMentions = extractMentionsIdsFromEditor(editor);
    const updatedContent: TextPromptWithInputItem = editorJsonToTextPrompt(editor.getJSON());

    // 首先处理删除的 mentions
    if (onBeforeDeleteMention && previousMentionsIds.current.length > 0) {
      const deletedMentionsIds: string[] = [];
      deletedMentionsIds.push(
        ...previousMentionsIds.current.filter((prev) => !currentMentions.some((curr) => curr === prev)),
      );

      // 如果存在删除的mention，则进行删除确认
      if (deletedMentionsIds.length > 0) {
        // 调用回调函数确认是否可以删除
        const canUpdateContent = onBeforeDeleteMention(deletedMentionsIds, updatedContent);

        // 如果不能删除，则恢复到上一次有效内容
        if (!canUpdateContent && lastValidContent.current !== null) {
          isInternalUpdate.current = true;
          editor.commands.setContent(lastValidContent.current);
          setTimeout(() => {
            isInternalUpdate.current = false;
          }, 0);
          return false;
        }
      }
    }

    // 处理新增的 mentions
    if (onBeforeAddMention && currentMentions.length > 0) {
      const addedMentionsIds = currentMentions.filter(
        (curr) => !previousMentionsIds.current.some((prev) => prev === curr),
      );

      // 如果存在新增的 mention，则进行添加确认
      if (addedMentionsIds.length > 0) {
        // 找出新增的 mention 对应的完整信息
        for (const mentionId of addedMentionsIds) {
          const mentionItem = updatedContent.find((item) => item.type === 'mention' && item.mention.id === mentionId);

          if (mentionItem?.type === 'mention') {
            // 调用回调确认是否可以添加
            const canAdd = onBeforeAddMention({
              id: mentionItem.mention.id,
              label: mentionItem.mention.name,
            });

            // 如果不能添加，则转为普通文本节点
            if (!canAdd) {
              // 找到对应的 mention 节点并替换为文本节点
              editor
                .chain()
                .focus()
                .command(({ tr }) => {
                  // 查找所有 mention 节点
                  const mentionsPositions: {
                    from: number;
                    to: number;
                    attrs: { id?: string; label?: string; color?: string };
                  }[] = [];
                  tr.doc.descendants((node, pos) => {
                    if (node.type.name === 'mention' && node.attrs.id === mentionId) {
                      mentionsPositions.push({
                        from: pos,
                        to: pos + node.nodeSize,
                        attrs: node.attrs,
                      });
                    }
                    return true;
                  });

                  // 从后往前替换，避免位置偏移
                  for (let i = mentionsPositions.length - 1; i >= 0; i--) {
                    const { from, to, attrs } = mentionsPositions[i];
                    const textContent = `@${attrs.label}`;
                    tr.replaceWith(from, to, editor.schema.text(textContent));
                  }

                  return false;
                })
                .run();

              return false;
            }
          }
        }
      }
    }

    // 更新引用以便下次比较
    previousMentionsIds.current = currentMentions;
    return true;
  };

  // 创建Mention扩展的配置
  const mentionExtension = CustomMention.configure({
    deleteTriggerWithBackspace: true,
    ...(mentionClassName ? ({ className: mentionClassName } as Record<string, string>) : {}),
    ...(mentionVariant ? ({ variant: mentionVariant } as Record<string, string>) : {}),
    ...customMentionOptions,
    suggestion: {
      allowedPrefixes: null,
      items: async ({ query }: { query: string }) => {
        // 优先使用自定义搜索函数
        if (lastSearchFn.current) {
          const result = await lastSearchFn.current(query);
          mentionListRef.current = result;
          return result;
        }
        return await defaultSearchEntities(query);
      },
      render: () => {
        let reactRenderer: ReactRenderer | null = null;
        let popup: TippyInstance[] | null = null;

        // 创建一个安全的 getReferenceClientRect 函数，处理 null 情况
        const createSafeGetReferenceClientRect = (clientRect: (() => DOMRect | null) | null): (() => DOMRect) => {
          return () => {
            if (!clientRect) {
              // 如果 clientRect 为 null，返回一个默认的 DOMRect
              return new DOMRect(0, 0, 0, 0);
            }
            const rect = clientRect();
            // 如果 clientRect() 返回 null，也返回一个默认的 DOMRect
            return rect || new DOMRect(0, 0, 0, 0);
          };
        };

        return {
          onStart: (props) => {
            // 如果 clientRect 为 null，不创建 popup
            if (!props.clientRect) {
              return;
            }

            reactRenderer = new ReactRenderer(MentionList, {
              props: {
                ...props,
                disableTooltip,
              },
              editor: props.editor,
            });

            popup = [
              tippy(document.querySelector('body') as Element, {
                getReferenceClientRect: createSafeGetReferenceClientRect(props.clientRect),
                appendTo: () => {
                  if (typeof popupContainer === 'function') return popupContainer();
                  if (popupContainer) return popupContainer;
                  return document.body;
                },
                content: reactRenderer.element,
                showOnCreate: true,
                interactive: true,
                trigger: 'manual',
                placement: 'bottom-start',
                theme: 'dark',
                zIndex,
              }),
            ];
          },
          onUpdate(props) {
            if (!reactRenderer) return;

            // 如果 clientRect 为 null，隐藏 popup 并返回
            if (!props.clientRect) {
              if (popup?.[0]) {
                popup[0].hide();
              }
              return;
            }

            reactRenderer.updateProps(props);

            if (popup?.[0]) {
              popup[0].setProps({
                getReferenceClientRect: createSafeGetReferenceClientRect(props.clientRect),
              });
            }
          },
          onKeyDown({ event }: { event: KeyboardEvent }) {
            if (event.key === 'Escape') {
              if (popup?.[0]) {
                popup[0].hide();
                // 显式阻止事件冒泡，防止父组件（如 Dialog）捕获并处理 ESC 事件
                event.stopPropagation();
                return true;
              }
            }

            if (reactRenderer?.ref) {
              return (reactRenderer.ref as { onKeyDown: (e: { key: string }) => boolean }).onKeyDown({
                key: event.key,
              });
            }

            return false;
          },
          onExit() {
            if (popup?.[0]) {
              popup[0].destroy();
              popup = null;
            }

            if (reactRenderer) {
              reactRenderer.destroy();
              reactRenderer = null;
            }
          },
        };
      },
      command: ({ editor, range, props }) => {
        const mentionItem = props;

        if (!mentionItem?.id || !mentionItem?.label) return;

        const canAdd = onBeforeAddMention?.(mentionItem as MentionItem) ?? true;

        if (canAdd) {
          const currentMentionItem = mentionListRef.current.find((item) => item.id === mentionItem.id);
          if (!currentMentionItem) return;
          editor
            .chain()
            .focus()
            .deleteRange(range)
            .insertContent({
              type: 'mention',
              attrs: {
                id: mentionItem.id,
                label: mentionItem.label,
                preview: currentMentionItem.preview ?? null,
                mediaType: currentMentionItem.mediaType ?? currentMentionItem.type ?? null,
                color:
                  mentionColor ||
                  getMentionColor({
                    element: (currentMentionItem?.element || currentMentionItem?.prompt) as ViduV1Element,
                    type: currentMentionItem?.type,
                  }),
              },
            })
            .run();
          onMentionInsert?.(currentMentionItem);
        }
      },
    },
    ...customMentionOptions,
  });

  return { mentionExtension, onUpdateCallback };
};
